import React, { useCallback } from 'react';
import { useField } from 'react-final-form';

import BlockFormNavigation from '@dashboard/components/BlockPageNavigation/BlockFormNavigation';
import Form from '@dashboard/components/form/Form';
import { usePlatformSendingStatusAlertPresent } from '@dashboard/components/PlatformSendingStatusAlert';
import {
  type TemplateBlockInput,
  type UpdateWaypointTemplateMutationVariables,
  useUpdateWaypointTemplateMutation,
} from '@dashboard/generated/wp/platform';
import { Grid } from '@mui/material';
import { AddBlockMenuStoreProvider } from '@wp-block-builder/stores/AddBlockMenuStore';
import { WaypointDocumentEditorStoreProvider } from '@wp-block-builder/stores/WaypointDocumentEditorStore';
import { WaypointDocumentStoreProvider } from '@wp-block-builder/stores/WaypointDocumentStore';

import { type TInternalSubmitData } from '../useInitialValues';

import FormFields from './FormFields';
import { useCurrentLayoutConfiguration } from './hooks';
import Sidebar from './Sidebar';

export type TSubmitData = UpdateWaypointTemplateMutationVariables;

type Props = {
  initialValues: TInternalSubmitData;
};

function useContainerHeightStyle() {
  const p = usePlatformSendingStatusAlertPresent();
  if (p) {
    return 'calc(100vh - 48px - 32px)';
  }
  return 'calc(100vh - 48px)';
}

function normalizeBlock(block: TemplateBlockInput): TemplateBlockInput {
  return {
    ...block,
    props: block.props || {},
    sectionProps: block.sectionProps || {},
    style: block.style || {},
  };
}

export default function WaypointTemplateForm({ initialValues }: Props) {
  const [updateTemplate] = useUpdateWaypointTemplateMutation();
  const containerHeight = useContainerHeightStyle();

  const onSubmitHandler = useCallback(
    async (form: TInternalSubmitData) => {
      await updateTemplate({
        variables: {
          ...form,
          configuration: {
            ...form.configuration,
            subjectTemplate: form.configuration.subjectTemplate ?? '',
            blocks: form.configuration.blocks.map(normalizeBlock),
          },
          defaultPreviewVariables: JSON.parse(form.defaultPreviewVariables),
          testScenarios: (form.testScenarios || []).map(({ name, variables }) => ({
            name,
            variables: JSON.parse(variables),
          })),
        },
      });
    },
    [updateTemplate]
  );

  return (
    <Form id="wp-form" successMessage="Template saved" initialValues={initialValues} onSubmit={onSubmitHandler}>
      <WaypointDocumentEditorStoreProvider>
        <AddBlockMenuStoreProvider>
          <BlockFormNavigation message="Are you sure you want to leave the template builder?" />
          <Grid container height={containerHeight} padding={0} margin={0}>
            <Grid item xs={6} sm={6} md="auto" height="100%" padding={0} margin={0}>
              <ConnectedSidebar />
            </Grid>
            <Grid item xs height="100%" padding={0} margin={0} sx={{ overflowY: 'auto' }}>
              <FormFields />
            </Grid>
          </Grid>
        </AddBlockMenuStoreProvider>
      </WaypointDocumentEditorStoreProvider>
    </Form>
  );
}

const EMPTY_VARIABLES = {};

function ConnectedSidebar() {
  const configuration = useField('configuration').input.value;
  const { data } = useCurrentLayoutConfiguration();
  return (
    <WaypointDocumentStoreProvider
      variables={EMPTY_VARIABLES}
      skipLiquid={false}
      configuration={configuration}
      layoutConfiguration={data}
    >
      <Sidebar />
    </WaypointDocumentStoreProvider>
  );
}
